/**
 * Structured bilingual (EN / HE) Systems guides for MU BEGINNING.
 * Player-facing only — no internal item / mix / index numbers in the rendered text.
 *
 * Topics: fenrir-mix, vip, grand-reset, voice, quests, market,
 * expanded-inventory, online-bonus, battle-pass.
 *
 * Fenrir LIVE rates = FenrirMix.xml base (flat per stage for all VIP) +
 * ChaosItemMixRate account bonus (AL0 +0 / AL1 +5 / AL2 +10 / AL3 +15).
 * Rates below are pre-Luck roll chances shown to players.
 *
 * Block types consumed by Guides.jsx:
 *   p | h | note | warn · table · npc · itemcard · mix · timeline · results
 */

import { guideImg } from "./guideAssets";

const FAIL_FENRIR = {
  en: "Failure destroys all materials in the box, unless a Talisman of Chaos Assembly protects the mix (talisman is consumed; materials are kept).",
  he: "כישלון הורס את כל החומרים בקופסה, אלא אם Talisman of Chaos Assembly מגן על השילוב (הקמע נצרך; החומרים נשמרים).",
};

const FAIL_FENRIR_SHORT = {
  en: "Materials destroyed unless Chaos Assembly talisman protects (then talisman consumed, materials kept).",
  he: "חומרים נהרסים אלא אם קמע Chaos Assembly מגן (אז הקמע נצרך והחומרים נשמרים).",
};

const VIP_RATE_NOTE = {
  en: "Fenrir stage base rate is the same for all VIP tiers. Account VIP still adds Chaos mix bonus (+0 / +5 / +10 / +15). Talisman of Luck can add more (cap 100%).",
  he: "שיעור הבסיס לכל שלב Fenrir זהה לכל דרגות ה־VIP. בונוס VIP של החשבון עדיין מתווסף ל־Chaos (+0 / +5 / +10 / +15). Talisman of Luck יכול להוסיף עוד (תקרה 100%).",
};

/** LIVE actual N/B/S/G after account ChaosItemMixRate (player-facing, pre-Luck). */
const RATE = {
  fragment: {
    en: "Normal 70% · Bronze 75% · Silver 80% · Gold 85%",
    he: "Normal 70% · Bronze 75% · Silver 80% · Gold 85%",
  },
  broken: {
    en: "Normal 50% · Bronze 55% · Silver 60% · Gold 65%",
    he: "Normal 50% · Bronze 55% · Silver 60% · Gold 65%",
  },
  red: {
    en: "Normal 30% · Bronze 35% · Silver 40% · Gold 45%",
    he: "Normal 30% · Bronze 35% · Silver 40% · Gold 45%",
  },
  blue: {
    en: "Normal 70% · Bronze 75% · Silver 80% · Gold 85%",
    he: "Normal 70% · Bronze 75% · Silver 80% · Gold 85%",
  },
  black: {
    en: "Normal 70% · Bronze 75% · Silver 80% · Gold 85%",
    he: "Normal 70% · Bronze 75% · Silver 80% · Gold 85%",
  },
  gold: {
    en: "Normal 60% · Bronze 65% · Silver 70% · Gold 75%",
    he: "Normal 60% · Bronze 65% · Silver 70% · Gold 75%",
  },
};

const chaosGoblinNpc = () => ({
  type: "npc",
  img: guideImg.chaosGoblin,
  name: "Chaos Goblin",
  map: { en: "Noria", he: "Noria" },
  coords: "180, 103",
  note: {
    en: "Also stands in Arkania (232, 57). Talk to the Chaos Goblin to open the Chaos Machine — every Fenrir recipe below is combined here.",
    he: "עומד גם ב־Arkania (232, 57). דברו עם ה־Chaos Goblin כדי לפתוח את ה־Chaos Machine — כל מתכוני ה־Fenrir למטה משולבים כאן.",
  },
});

const jewelBundleHelper = () => [
  {
    type: "h",
    en: "Jewel bundles",
    he: "חבילות תכשיטים",
  },
  {
    type: "p",
    en: "Jewel packs can replace loose jewels. Bless / Soul packs of 10 / 20 / 30 and Chaos / Creation / Life equivalents count toward the same total. Bundles use the same success rate as loose jewels.",
    he: "חבילות תכשיטים יכולות להחליף תכשיטים בודדים. חבילות Bless / Soul של 10 / 20 / 30 ומקבילות Chaos / Creation / Life נספרות לאותו סה״כ. לחבילות אותו שיעור הצלחה כמו לתכשיטים בודדים.",
  },
  {
    type: "itemcard",
    img: guideImg.jewelBlessBundle,
    name: {
      en: "Jewel of Bless Bundle",
      he: "Jewel of Bless Bundle",
    },
    note: {
      en: "Packs of 10 / 20 / 30 count as that many Bless jewels.",
      he: "חבילות 10 / 20 / 30 נספרות כמספר זה של תכשיטי Bless.",
    },
  },
  {
    type: "itemcard",
    img: guideImg.jewelSoulBundle,
    name: {
      en: "Jewel of Soul Bundle",
      he: "Jewel of Soul Bundle",
    },
    note: {
      en: "Packs of 10 / 20 / 30 count as that many Soul jewels.",
      he: "חבילות 10 / 20 / 30 נספרות כמספר זה של תכשיטי Soul.",
    },
  },
];

export const guideSystemsContent = {
  "fenrir-mix": [
    {
      type: "h",
      en: "Fenrir Mix",
      he: "Fenrir Mix",
    },
    {
      type: "p",
      en: "Craft Fenrir step by step in the Chaos Machine. Farm Splinter of Armor, Bless of Guardian and Claw of Beast mainly from Kanturu (Battle of Maya / related maps).",
      he: "יוצרים Fenrir שלב־אחר־שלב ב־Chaos Machine. פארמו Splinter of Armor, Bless of Guardian ו־Claw of Beast בעיקר מ־Kanturu (Battle of Maya / מפות קשורות).",
    },
    chaosGoblinNpc(),
    {
      type: "note",
      ...VIP_RATE_NOTE,
    },
    {
      type: "itemcard",
      img: guideImg.talismanLuck,
      name: "Talisman of Luck",
      note: {
        en: "Optional — raises success chance on top of VIP (combined cap 100%).",
        he: "אופציונלי — מעלה את סיכוי ההצלחה מעל ה־VIP (תקרה משולבת 100%).",
      },
    },
    {
      type: "warn",
      ...FAIL_FENRIR,
    },

    {
      type: "h",
      en: "Overview",
      he: "סקירה",
    },
    {
      type: "timeline",
      steps: [
        {
          title: {
            en: "Step 1 — Fragment of Horn",
            he: "שלב 1 — Fragment of Horn",
          },
          body: {
            en: "Jewels ×10 each + Splinter ×20 + Bless of Guardian ×20 + 100,000,000 Zen.",
            he: "תכשיטים ×10 מכל סוג + Splinter ×20 + Bless of Guardian ×20 + 100,000,000 Zen.",
          },
          img: guideImg.fragmentHorn,
        },
        {
          title: {
            en: "Step 2 — Broken Horn",
            he: "שלב 2 — Broken Horn",
          },
          body: {
            en: "Jewels ×20 each + Claw ×20 + Fragment ×5 + 500,000,000 Zen.",
            he: "תכשיטים ×20 מכל סוג + Claw ×20 + Fragment ×5 + 500,000,000 Zen.",
          },
          img: guideImg.brokenHorn,
        },
        {
          title: {
            en: "Step 3 — Horn of Fenrir (Red)",
            he: "שלב 3 — Horn of Fenrir (Red)",
          },
          body: {
            en: "Jewels ×30 each + Broken Horn ×1 + 1,000,000,000 Zen.",
            he: "תכשיטים ×30 מכל סוג + Broken Horn ×1 + 1,000,000,000 Zen.",
          },
          img: guideImg.fenrirRed,
        },
        {
          title: {
            en: "Upgrades — Blue / Black / Gold",
            he: "שדרוגים — Blue / Black / Gold",
          },
          body: {
            en: "Upgrade Red → Blue → Black, then combine Black + Blue for Gold Fenrir.",
            he: "שדרגו Red → Blue → Black, ואז שלבו Black + Blue ל־Gold Fenrir.",
          },
          img: guideImg.fenrirGold,
        },
      ],
    },

    ...jewelBundleHelper(),

    {
      type: "h",
      en: "Step 1 — Fragment of Horn",
      he: "שלב 1 — Fragment of Horn",
    },
    {
      type: "mix",
      title: { en: "Fragment of Horn", he: "Fragment of Horn" },
      zen: {
        en: "100,000,000 Zen",
        he: "100,000,000 Zen",
      },
      rate: RATE.fragment,
      fail: FAIL_FENRIR_SHORT,
      ingredients: [
        { img: guideImg.jewelBless, name: "Jewel of Bless", qty: "×10" },
        { img: guideImg.jewelSoul, name: "Jewel of Soul", qty: "×10" },
        { img: guideImg.jewelChaos, name: "Jewel of Chaos", qty: "×10" },
        { img: guideImg.jewelCreation, name: "Jewel of Creation", qty: "×10" },
        { img: guideImg.jewelLife, name: "Jewel of Life", qty: "×10" },
        { img: guideImg.splinterArmor, name: "Splinter of Armor", qty: "×20" },
        { img: guideImg.blessGuardian, name: "Bless of Guardian", qty: "×20" },
      ],
      result: {
        img: guideImg.fragmentHorn,
        name: "Fragment of Horn",
        qty: "×1",
      },
    },
    {
      type: "note",
      en: "Bless Bundle +0 (=10) can replace 10 loose Jewel of Bless. Same idea for other jewel packs where allowed.",
      he: "Bless Bundle +0 (=10) יכול להחליף 10 תכשיטי Bless בודדים. אותו רעיון לחבילות תכשיטים אחרות כשהמקס מאפשר.",
    },

    {
      type: "h",
      en: "Step 2 — Broken Horn",
      he: "שלב 2 — Broken Horn",
    },
    {
      type: "mix",
      title: { en: "Broken Horn", he: "Broken Horn" },
      zen: {
        en: "500,000,000 Zen",
        he: "500,000,000 Zen",
      },
      rate: RATE.broken,
      fail: FAIL_FENRIR_SHORT,
      ingredients: [
        { img: guideImg.jewelBless, name: "Jewel of Bless", qty: "×20" },
        { img: guideImg.jewelSoul, name: "Jewel of Soul", qty: "×20" },
        { img: guideImg.jewelChaos, name: "Jewel of Chaos", qty: "×20" },
        { img: guideImg.jewelCreation, name: "Jewel of Creation", qty: "×20" },
        { img: guideImg.jewelLife, name: "Jewel of Life", qty: "×20" },
        { img: guideImg.clawBeast, name: "Claw of Beast", qty: "×20" },
        { img: guideImg.fragmentHorn, name: "Fragment of Horn", qty: "×5" },
      ],
      result: {
        img: guideImg.brokenHorn,
        name: "Broken Horn",
        qty: "×1",
      },
    },
    {
      type: "note",
      en: "Jewel bundles of 10 / 20 are OK for this step (same success rate as loose jewels).",
      he: "חבילות תכשיטים של 10 / 20 מתאימות לשלב הזה (אותו שיעור הצלחה כמו תכשיטים בודדים).",
    },

    {
      type: "h",
      en: "Step 3 — Horn of Fenrir (Red)",
      he: "שלב 3 — Horn of Fenrir (Red)",
    },
    {
      type: "mix",
      title: {
        en: "Horn of Fenrir (Red)",
        he: "Horn of Fenrir (Red)",
      },
      zen: {
        en: "1,000,000,000 Zen",
        he: "1,000,000,000 Zen",
      },
      rate: RATE.red,
      fail: FAIL_FENRIR_SHORT,
      ingredients: [
        { img: guideImg.jewelBless, name: "Jewel of Bless", qty: "×30" },
        { img: guideImg.jewelSoul, name: "Jewel of Soul", qty: "×30" },
        { img: guideImg.jewelChaos, name: "Jewel of Chaos", qty: "×30" },
        { img: guideImg.jewelCreation, name: "Jewel of Creation", qty: "×30" },
        { img: guideImg.jewelLife, name: "Jewel of Life", qty: "×30" },
        { img: guideImg.brokenHorn, name: "Broken Horn", qty: "×1" },
      ],
      result: {
        img: guideImg.fenrirRed,
        name: {
          en: "Horn of Fenrir (Red)",
          he: "Horn of Fenrir (Red)",
        },
        qty: "×1",
        variant: "pet",
      },
    },

    {
      type: "h",
      en: "Upgrade — Blue Fenrir",
      he: "שדרוג — Blue Fenrir",
    },
    {
      type: "mix",
      title: { en: "Blue Fenrir", he: "Blue Fenrir" },
      zen: {
        en: "10,000,000 Zen",
        he: "10,000,000 Zen",
      },
      rate: RATE.blue,
      fail: FAIL_FENRIR_SHORT,
      ingredients: [
        {
          img: guideImg.fenrirRed,
          name: {
            en: "Horn of Fenrir (Red)",
            he: "Horn of Fenrir (Red)",
          },
          qty: "×1",
          variant: "pet",
        },
        { img: guideImg.jewelSoul, name: "Jewel of Soul", qty: "×90" },
        { img: guideImg.jewelBless, name: "Jewel of Bless", qty: "×90" },
        { img: guideImg.jewelChaos, name: "Jewel of Chaos", qty: "×60" },
        { img: guideImg.jewelLife, name: "Jewel of Life", qty: "×60" },
      ],
      result: {
        img: guideImg.fenrirBlue,
        name: {
          en: "Horn of Fenrir (Blue)",
          he: "Horn of Fenrir (Blue)",
        },
        qty: "×1",
        variant: "pet",
      },
    },

    {
      type: "h",
      en: "Upgrade — Black Fenrir",
      he: "שדרוג — Black Fenrir",
    },
    {
      type: "mix",
      title: { en: "Black Fenrir", he: "Black Fenrir" },
      zen: {
        en: "10,000,000 Zen",
        he: "10,000,000 Zen",
      },
      rate: RATE.black,
      fail: FAIL_FENRIR_SHORT,
      ingredients: [
        {
          img: guideImg.fenrirBlue,
          name: {
            en: "Horn of Fenrir (Blue)",
            he: "Horn of Fenrir (Blue)",
          },
          qty: "×1",
          variant: "pet",
        },
        { img: guideImg.jewelSoul, name: "Jewel of Soul", qty: "×90" },
        { img: guideImg.jewelBless, name: "Jewel of Bless", qty: "×90" },
        { img: guideImg.jewelChaos, name: "Jewel of Chaos", qty: "×60" },
        { img: guideImg.jewelCreation, name: "Jewel of Creation", qty: "×60" },
      ],
      result: {
        img: guideImg.fenrirBlack,
        name: {
          en: "Horn of Fenrir (Black)",
          he: "Horn of Fenrir (Black)",
        },
        qty: "×1",
        variant: "pet",
      },
    },

    {
      type: "h",
      en: "Upgrade — Gold Fenrir",
      he: "שדרוג — Gold Fenrir",
    },
    {
      type: "mix",
      title: { en: "Gold Fenrir", he: "Gold Fenrir" },
      zen: {
        en: "0 Zen",
        he: "0 Zen",
      },
      rate: RATE.gold,
      fail: FAIL_FENRIR_SHORT,
      ingredients: [
        {
          img: guideImg.fenrirBlack,
          name: {
            en: "Horn of Fenrir (Black)",
            he: "Horn of Fenrir (Black)",
          },
          qty: "×1",
          variant: "pet",
        },
        {
          img: guideImg.fenrirBlue,
          name: {
            en: "Horn of Fenrir (Blue)",
            he: "Horn of Fenrir (Blue)",
          },
          qty: "×1",
          variant: "pet",
        },
      ],
      result: {
        img: guideImg.fenrirGold,
        name: {
          en: "Horn of Fenrir (Gold)",
          he: "Horn of Fenrir (Gold)",
        },
        qty: "×1",
        variant: "pet",
      },
    },

    {
      type: "h",
      en: "LIVE success rates (summary)",
      he: "שיעורי הצלחה חיים (סיכום)",
    },
    {
      type: "table",
      columns: [
        { key: "stage", en: "Stage", he: "שלב" },
        { key: "n", en: "Normal", he: "Normal" },
        { key: "b", en: "Bronze", he: "Bronze" },
        { key: "s", en: "Silver", he: "Silver" },
        { key: "g", en: "Gold", he: "Gold" },
      ],
      rows: [
        {
          stage: { en: "Fragment of Horn", he: "Fragment of Horn" },
          n: "70%",
          b: "75%",
          s: "80%",
          g: "85%",
        },
        {
          stage: { en: "Broken Horn", he: "Broken Horn" },
          n: "50%",
          b: "55%",
          s: "60%",
          g: "65%",
        },
        {
          stage: {
            en: "Horn of Fenrir (Red)",
            he: "Horn of Fenrir (Red)",
          },
          n: "30%",
          b: "35%",
          s: "40%",
          g: "45%",
        },
        {
          stage: { en: "Blue Fenrir", he: "Blue Fenrir" },
          n: "70%",
          b: "75%",
          s: "80%",
          g: "85%",
        },
        {
          stage: { en: "Black Fenrir", he: "Black Fenrir" },
          n: "70%",
          b: "75%",
          s: "80%",
          g: "85%",
        },
        {
          stage: { en: "Gold Fenrir", he: "Gold Fenrir" },
          n: "60%",
          b: "65%",
          s: "70%",
          g: "75%",
        },
      ],
    },
    {
      type: "note",
      ...VIP_RATE_NOTE,
    },
    {
      type: "note",
      en: "Farm tip: Splinter of Armor, Bless of Guardian and Claw of Beast drop from Kanturu (Battle of Maya / related maps).",
      he: "טיפ פארם: Splinter of Armor, Bless of Guardian ו־Claw of Beast נופלים מ־Kanturu (Battle of Maya / מפות קשורות).",
    },
  ],

  vip: [
    {
      type: "h",
      en: "VIP System",
      he: "מערכת VIP",
    },
    {
      type: "p",
      en: "Buy VIP in-game with the End key (Custom Buy VIP). Each package lasts 30 days.",
      he: "קנו VIP במשחק עם מקש End (Custom Buy VIP). כל חבילה ל־30 יום.",
    },
    {
      type: "table",
      columns: [
        { key: "tier", en: "Tier", he: "דרגה" },
        { key: "exp", en: "Experience", he: "Experience" },
        { key: "drop", en: "Drop", he: "Drop" },
        { key: "cost", en: "Cost", he: "מחיר" },
      ],
      rows: [
        {
          tier: "VIP Bronze",
          exp: "+10%",
          drop: "+10%",
          cost: "300 Credits",
        },
        {
          tier: "VIP Silver",
          exp: "+20%",
          drop: "+20%",
          cost: "600 Credits",
        },
        {
          tier: "VIP Gold",
          exp: "+30%",
          drop: "+30%",
          cost: "1000 Credits",
        },
      ],
    },
    {
      type: "note",
      en: "Higher VIP also improves some craft success rates (example: Fenrir Mix) and dungeon daily entry caps.",
      he: "VIP גבוה יותר משפר גם חלק משיעורי ההצלחה בקראפט (למשל Fenrir Mix) ואת מגבלות הכניסה היומיות למבוכים.",
    },
    {
      type: "p",
      en: "Account-level EXP / Drop bonuses from VIP tiers stack with server rates according to GameServer settings.",
      he: "בונוסי EXP / Drop ברמת חשבון מדרגות VIP מצטברים עם רייטי השרת לפי הגדרות ה־GameServer.",
    },
  ],

  "grand-reset": [
    {
      type: "h",
      en: "Grand Reset (Master Reset)",
      he: "Grand Reset (Master Reset)",
    },
    {
      type: "p",
      en: "Command: /greset. Completing a Grand Reset returns your reset count to 0 so you can climb the reset ladder again.",
      he: "פקודה: /greset. השלמת Grand Reset מחזירה את מונה הריסטים ל־0 כדי שתוכלו לטפס שוב בסולם.",
    },
    {
      type: "table",
      columns: [
        { key: "k", en: "Detail", he: "פרט" },
        { key: "v", en: "Value", he: "ערך" },
      ],
      rows: [
        {
          k: { en: "Requirement", he: "דרישה" },
          v: { en: "50 resets completed", he: "50 ריסטים הושלמו" },
        },
        {
          k: { en: "Cost", he: "עלות" },
          v: "2,000,000,000 Zen",
        },
        {
          k: { en: "Reward", he: "תגמול" },
          v: {
            en: "15,000 reset points",
            he: "15,000 נקודות ריסט",
          },
        },
        {
          k: { en: "Server limit", he: "מגבלת שרת" },
          v: {
            en: "1 Grand Reset maximum",
            he: "מקסימום Grand Reset אחד",
          },
        },
      ],
    },
    {
      type: "note",
      en: "After Grand Reset, Reset Table Section 1 applies (higher Zen costs on later resets). See the Reset System guide for the full table.",
      he: "אחרי Grand Reset חל Section 1 של טבלת הריסט (עלויות Zen גבוהות יותר בריסטים מאוחרים). ראו את מדריך Reset System לטבלה המלאה.",
    },
  ],

  voice: [
    {
      type: "h",
      en: "Voice Chat",
      he: "צ'אט קולי",
    },
    {
      type: "p",
      en: "Proximity voice chat is enabled on Mu Beginning — talk with nearby players using your microphone.",
      he: "צ'אט קולי לפי קרבה מופעל ב־Mu Beginning — דברו עם שחקנים קרובים דרך המיקרופון.",
    },
    {
      type: "table",
      columns: [
        { key: "k", en: "Setting", he: "הגדרה" },
        { key: "v", en: "Value", he: "ערך" },
      ],
      rows: [
        {
          k: { en: "Push-to-talk", he: "Push-to-talk" },
          v: {
            en: "F1 (hold to speak)",
            he: "F1 (החזיקו כדי לדבר)",
          },
        },
        {
          k: { en: "Voice radius", he: "רדיוס קול" },
          v: "50",
        },
        {
          k: { en: "Available for", he: "זמין ל" },
          v: {
            en: "Normal, VIP and GM accounts",
            he: "חשבונות Normal, VIP ו־GM",
          },
        },
      ],
    },
    {
      type: "note",
      en: "Use a working mic / headset in Windows, then hold F1 in-game while near other players.",
      he: "השתמשו במיקרופון / אוזניות תקינים ב־Windows, ואז החזיקו F1 במשחק ליד שחקנים אחרים.",
    },
  ],

  quests: [
    {
      type: "h",
      en: "Quest Systems",
      he: "מערכות קווסטים",
    },
    {
      type: "p",
      en: "Several quest systems run in parallel on our server.",
      he: "בשרת שלנו רצות במקביל כמה מערכות קווסטים.",
    },
    {
      type: "h",
      en: "Daily Quest System",
      he: "מערכת קווסטים יומית",
    },
    {
      type: "p",
      en: "Kill / farm quests (example targets: spiders, goblins and higher maps). Open the Quest System UI for current objectives and rewards.",
      he: "קווסטי הריגה / פארם (למשל עכבישים, גובלינים ומפות גבוהות יותר). פתחו את ממשק ה־Quest System למטרות ותגמולים נוכחיים.",
    },
    {
      type: "h",
      en: "Quest NPC",
      he: "Quest NPC",
    },
    {
      type: "p",
      en: "Custom Quest NPCs are placed on multiple maps for kill / collect style quests.",
      he: "NPCs מותאמים מסוג Quest מוצבים במפות שונות לקווסטי הריגה / איסוף.",
    },
    {
      type: "h",
      en: "Quest World / Gens",
      he: "Quest World / Gens",
    },
    {
      type: "p",
      en: "Shadow Phantom Soldier and Gens steward NPCs handle world / gens-style progression quests.",
      he: "NPCs מסוג Shadow Phantom Soldier ו־Gens steward מטפלים בקווסטי התקדמות עולם / גנס.",
    },
    {
      type: "h",
      en: "Classic class quests",
      he: "קווסטי מחלקה קלאסיים",
    },
    {
      type: "p",
      en: "Sebina the Priestess, Marlon and Apostle Devin handle 2nd / 3rd class (where applicable). Elf Soldier / classic soldier quests appear in the standard MU chain — follow NPC dialogue in-game. See the Class Change guides for full walkthroughs.",
      he: "Sebina the Priestess, Marlon ו־Apostle Devin מטפלים ב־2nd / 3rd class (לפי הדמות). קווסטי Elf Soldier / חיילים קלאסיים מופיעים בשרשרת הסטנדרטית של MU — עקבו אחרי דיאלוג ה־NPC במשחק. ראו את מדריכי Class Change למדריכים מלאים.",
    },
    {
      type: "note",
      en: "Battle Pass also adds daily / weekly quests tied to events, kills and jewel pickups.",
      he: "ה־Battle Pass מוסיף גם קווסטים יומיים / שבועיים הקשורים לאירועים, הריגות ואיסוף תכשיטים.",
    },
  ],

  market: [
    {
      type: "h",
      en: "Market System",
      he: "מערכת Market",
    },
    {
      type: "p",
      en: "The Market is the player trading system — list items for sale so others can buy them while you are offline.",
      he: "ה־Market הוא מערכת המסחר בין שחקנים — מפרסמים פריטים למכירה כדי שאחרים יקנו גם כשאתם אופליין.",
    },
    {
      type: "table",
      columns: [
        { key: "k", en: "Detail", he: "פרט" },
        { key: "v", en: "Value", he: "ערך" },
      ],
      rows: [
        {
          k: { en: "Listing duration", he: "משך פרסום" },
          v: {
            en: "You choose (days)",
            he: "אתם בוחרים (ימים)",
          },
        },
        {
          k: { en: "Maximum listing", he: "מקסימום פרסום" },
          v: {
            en: "30 days",
            he: "30 יום",
          },
        },
        {
          k: { en: "Seller tax", he: "מס מוכר" },
          v: {
            en: "Approx. 5–15% by item category",
            he: "בערך 5–15% לפי קטגוריית פריט",
          },
        },
      ],
    },
    {
      type: "note",
      en: "Expired listings can be kept for the seller but hidden from buyers — reclaim your items if they do not sell.",
      he: "פרסומים שפגו יכולים להישמר למוכר אך להיות מוסתרים מקונים — אספו חזרה פריטים שלא נמכרו.",
    },
  ],

  "expanded-inventory": [
    {
      type: "h",
      en: "Expanded Inventory",
      he: "Expanded Inventory",
    },
    {
      type: "p",
      en: "Expanded Inventory unlocks extra inventory space using the Magic Backpack item.",
      he: "Expanded Inventory פותח מקום אינבנטורי נוסף באמצעות הפריט Magic Backpack.",
    },
    {
      type: "itemcard",
      name: "Magic Backpack",
      note: {
        en: "Duration: 30 days per unlock item use. Use it when your main inventory is full during long farm sessions or event looting.",
        he: "משך: 30 יום לכל שימוש בפריט הפתיחה. השתמשו בו כשהאינבנטורי הראשי מלא בסשני פארם ארוכים או בלוט מאירועים.",
      },
    },
  ],

  "online-bonus": [
    {
      type: "h",
      en: "Online Bonus",
      he: "Online Bonus",
    },
    {
      type: "p",
      en: "Stay online to receive automatic rewards.",
      he: "הישארו אונליין כדי לקבל תגמולים אוטומטיים.",
    },
    {
      type: "table",
      columns: [
        { key: "k", en: "Detail", he: "פרט" },
        { key: "v", en: "Value", he: "ערך" },
      ],
      rows: [
        {
          k: { en: "Interval", he: "מרווח" },
          v: {
            en: "Every 60 minutes online",
            he: "כל 60 דקות אונליין",
          },
        },
        {
          k: { en: "Reward", he: "תגמול" },
          v: {
            en: "1 Credit per interval",
            he: "1 Credit לכל מרווח",
          },
        },
      ],
    },
    {
      type: "note",
      en: "Remain connected on the GameServer; the timer advances while your character is online. Disconnecting pauses progress toward the next payout.",
      he: "הישארו מחוברים ל־GameServer; הטיימר מתקדם כשהדמות אונליין. ניתוק עוצר את ההתקדמות לתשלום הבא.",
    },
  ],

  "battle-pass": [
    {
      type: "h",
      en: "Battle Pass",
      he: "Battle Pass",
    },
    {
      type: "p",
      en: "Our Battle Pass runs in seasonal cycles of 90 days. Progress by completing daily / weekly quests and earning Battle Pass levels (up to level 50).",
      he: "ה־Battle Pass שלנו רץ במחזורי עונה של 90 יום. מתקדמים בהשלמת קווסטים יומיים / שבועיים ובצבירת רמות Battle Pass (עד רמה 50).",
    },
    {
      type: "p",
      en: "There are two tracks: Free (available to everyone) and Premium (paid upgrade with better rewards).",
      he: "יש שני מסלולים: Free (לכולם) ו־Premium (שדרוג בתשלום עם תגמולים טובים יותר).",
    },
    {
      type: "table",
      columns: [
        { key: "k", en: "Detail", he: "פרט" },
        { key: "v", en: "Value", he: "ערך" },
      ],
      rows: [
        {
          k: { en: "Premium pass", he: "מסלול Premium" },
          v: "600 BCoin",
        },
        {
          k: { en: "Buy level", he: "קניית רמה" },
          v: {
            en: "200 BCoin per level",
            he: "200 BCoin לרמה",
          },
        },
        {
          k: { en: "Level buy limit", he: "מגבלת קניית רמות" },
          v: {
            en: "Max 2 purchases per day",
            he: "עד 2 רכישות ביום",
          },
        },
        {
          k: { en: "Max level", he: "רמה מקסימלית" },
          v: "50",
        },
        {
          k: { en: "Season length", he: "אורך עונה" },
          v: {
            en: "90 days",
            he: "90 יום",
          },
        },
      ],
    },
    {
      type: "note",
      en: "Rewards include jewels, Kundun boxes and exclusive Premium rewards (including pets on higher Premium levels).",
      he: "התגמולים כוללים תכשיטים, קופסאות Kundun ותגמולי Premium בלעדיים (כולל חיות מחמד ברמות Premium גבוהות).",
    },
    {
      type: "p",
      en: "Stay logged in and complete event / kill / jewel quests listed in the Battle Pass window to advance faster.",
      he: "הישארו מחוברים והשלימו קווסטי אירועים / הריגות / תכשיטים שמופיעים בחלון ה־Battle Pass כדי להתקדם מהר יותר.",
    },
  ],
};

export default guideSystemsContent;
